import { Bank, CheckCircle, WarningCircle, CurrencyDollar, Briefcase, Clock } from "@phosphor-icons/react";

const SimpleRow = ({ label, value }) => (
  <div className="flex justify-between items-start py-2.5 border-b border-zinc-800/50 last:border-b-0">
    <span className="text-xs text-zinc-400">{label}</span>
    <span className="text-xs text-zinc-100 font-medium text-right max-w-[60%]">{value}</span>
  </div>
);

const PointList = ({ items, tone = "good" }) => (
  <ul className="space-y-2">
    {items.map((it, i) => (
      <li key={i} className="flex items-start gap-2 text-xs text-zinc-300 leading-relaxed">
        {tone === "good"
          ? <CheckCircle size={13} weight="fill" className="text-emerald-400 shrink-0 mt-0.5" />
          : <WarningCircle size={13} weight="fill" className="text-red-400 shrink-0 mt-0.5" />}
        <span>{it}</span>
      </li>
    ))}
  </ul>
);

export default function Retire401k() {
  return (
    <div className="p-5 max-w-[1400px] space-y-6" data-testid="retire-401k-page">
      {/* Hero */}
      <div className="bg-zinc-900/50 border border-zinc-800/80 rounded-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-sm bg-blue-900/20 flex items-center justify-center">
            <Briefcase size={20} weight="bold" className="text-blue-400" />
          </div>
          <div>
            <h2 className="text-lg font-heading font-bold text-zinc-50">401(k)</h2>
            <p className="text-xs text-zinc-400">Employer-sponsored retirement account for private sector workers</p>
          </div>
        </div>
        <p className="text-sm text-zinc-300 leading-relaxed">
          A 401(k) is a retirement account offered by most private companies. You contribute money straight from your paycheck (before taxes), your employer often matches part of what you put in, and the money grows tax-deferred until you withdraw it after age 59½.
        </p>
      </div>

      {/* Quick facts */}
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-5">
        <h3 className="text-sm font-heading font-bold text-zinc-100 mb-3 flex items-center gap-2">
          <Clock size={14} weight="bold" className="text-blue-400" /> Quick Facts (2026)
        </h3>
        <SimpleRow label="Who can use it" value="Employees of companies that offer one" />
        <SimpleRow label="2026 contribution limit" value="$23,500 (under 50) · $31,000 (50+)" />
        <SimpleRow label="Employer match" value="Common: 50%–100% up to 3%–6% of salary" />
        <SimpleRow label="Tax treatment (Traditional)" value="Pre-tax now, taxed on withdrawal" />
        <SimpleRow label="Tax treatment (Roth 401k)" value="After-tax now, tax-free on withdrawal" />
        <SimpleRow label="Withdraw without penalty" value="Age 59½" />
        <SimpleRow label="Early withdrawal penalty" value="10% + income tax" />
        <SimpleRow label="Required withdrawals (RMDs)" value="Start at age 73" />
      </div>

      {/* How it works */}
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-5">
        <h3 className="text-sm font-heading font-bold text-zinc-100 mb-3 flex items-center gap-2">
          <CurrencyDollar size={14} weight="bold" className="text-emerald-400" /> How it Works
        </h3>
        <ol className="space-y-2.5 text-xs text-zinc-300 leading-relaxed list-decimal list-inside">
          <li>You choose a % of your paycheck to contribute (e.g., 6%).</li>
          <li>That money goes into your 401(k) before income tax is taken.</li>
          <li>Your employer adds their match (free money — always take this).</li>
          <li>You pick investments from a menu (target-date funds, index funds, etc.).</li>
          <li>The money grows tax-deferred. You only pay tax when you withdraw it in retirement.</li>
        </ol>
      </div>

      {/* Pros / Cons */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-emerald-900/10 border border-emerald-800/30 rounded-sm p-5">
          <h3 className="text-sm font-heading font-bold text-emerald-300 mb-3">Pros</h3>
          <PointList items={[
            "Free money via employer match — instant 50%-100% return.",
            "High contribution limits ($23,500 in 2026).",
            "Pre-tax contributions lower your taxable income today.",
            "Automatic — deducted from paycheck so you never see it.",
            "Money is protected from lawsuits and bankruptcy in most states.",
          ]} />
        </div>
        <div className="bg-red-900/10 border border-red-800/30 rounded-sm p-5">
          <h3 className="text-sm font-heading font-bold text-red-300 mb-3">Cons</h3>
          <PointList tone="bad" items={[
            "Limited investment menu (whatever your employer offers).",
            "10% penalty if you withdraw before age 59½.",
            "Some plans charge high fees inside the investment options.",
            "Roth option is not offered by every employer.",
            "Job change = you may need to roll it over.",
          ]} />
        </div>
      </div>

      {/* Bottom line */}
      <div className="bg-amber-900/10 border border-amber-800/30 rounded-sm p-5">
        <h3 className="text-sm font-heading font-bold text-amber-300 mb-2 flex items-center gap-2">
          <Bank size={14} weight="bold" /> Bottom Line
        </h3>
        <p className="text-xs text-zinc-300 leading-relaxed">
          If your employer offers a 401(k) match, contribute at least enough to get the full match. That match is the highest-return investment you'll ever make. Even if there's no match, the tax advantage makes a 401(k) one of the best retirement tools available.
        </p>
      </div>
    </div>
  );
}
